// @flow

import { getCurrencySymbol } from 'shared/utils';

export type BomPrintReportType =
  | 'single-level'
  | 'indented'
  | 'summarized'
  | 'costed';

export type FishbowlBomRow = {
  itemNumber: string,
  type: string,
  description: string,
  uom: string,
  quantity: number,
  scrapPercent: number,
  findNumber: string,
  level: number,
  levelLabel: string,
  averageCost: number,
  costOfBill: number,
  occur: number,
  notes: string,
};

export type BomPrintColumn = {
  key: string,
  label: string,
  align: 'left' | 'right',
};

export type BomPrintReport = {
  reportTitle: string,
  reportSubtitle: string,
  columns: Array<BomPrintColumn>,
  rows: Array<FishbowlBomRow>,
  footerNotes: Array<string>,
  totalCost: ?number,
  maxLevel: number,
};

export type BomPrintParentInfo = {
  itemNumber: string,
  bomNumber: string,
  name: string,
  description: string,
  uom: string,
  bomTypeLabel: string,
  companyName: string,
};

const REPORT_TITLES: { [BomPrintReportType]: string } = {
  'single-level': 'Single-Level Bill of Materials Report',
  indented: 'Indented Bill of Material Report',
  summarized: 'Summarized Bill of Materials Report',
  costed: 'Costed Bill of Materials Report',
};

const REPORT_SUBTITLES: { [BomPrintReportType]: string } = {
  'single-level': 'With Phantom Blow Through',
  indented: '', // filled with level count
  summarized: '', // filled with level count
  costed: 'Single-Level With Phantom Blow Through',
};

export function formatQty(value: number): string {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

export function formatScrap(scrapFactor: ?number): string {
  const value = scrapFactor || 0;
  return `${Number(value).toFixed(3)}%`;
}

export function formatCost(value: ?number): string {
  if (value === null || value === undefined) {
    return '';
  }
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

export function formatLevelLabel(level: number): string {
  if (level < 1) {
    return '';
  }
  return `${'-'.repeat(level)}${level}`;
}

function mapFishbowlType(item: Object): string {
  if (item.isPhantom) {
    return 'Phantom';
  }

  const typeMap = {
    manufacturing: 'STD',
    engineering: 'ENG',
    configurable: 'CFG',
    sales: 'SALES',
    service: 'SVC',
  };

  return typeMap[item.bomType] || 'STD';
}

function getItemDescription(item: Object): string {
  return (
    item.description ||
    item.salesDisplayName ||
    item.optionLabel ||
    item.name ||
    ''
  );
}

function getItemUom(item: Object): string {
  return (
    item.quantityUOM?.symbol ||
    item.quantityUOM?.name ||
    ''
  ).toUpperCase();
}

function getItemCost(item: Object): number {
  const cost =
    item.cost != null
      ? item.cost
      : item.totalCalculatedCost != null
        ? item.totalCalculatedCost
        : item.componentPrice;
  return cost != null ? Number(cost) : 0;
}

function itemToFishbowlRow(
  item: Object,
  quantity: number,
  level: number,
  occur: number = 1,
): FishbowlBomRow {
  const averageCost = getItemCost(item);
  const costOfBill = quantity * averageCost;

  return {
    itemNumber: item.itemNumber || '',
    type: mapFishbowlType(item),
    description: getItemDescription(item),
    uom: getItemUom(item),
    quantity,
    scrapPercent: item.scrapFactor || 0,
    findNumber: item.findNumber || '',
    level,
    levelLabel: formatLevelLabel(level),
    averageCost,
    costOfBill,
    occur,
    notes: item.notes || '',
  };
}

function getMaxTreeLevel(nodes: ?Array<Object>, level: number = 1): number {
  if (!nodes || !nodes.length) {
    return 0;
  }

  let max = level;
  nodes.forEach(node => {
    if (node.children && node.children.length) {
      max = Math.max(max, getMaxTreeLevel(node.children, level + 1));
    }
  });

  return max;
}

function collectFooterNotes(rows: Array<FishbowlBomRow>): Array<string> {
  const notes = rows
    .map(row => row.notes.trim())
    .filter(note => note.length > 0);

  return Array.from(new Set(notes));
}

/**
 * Single-level BOM with phantom blow-through: phantoms are replaced by their children
 * at the same level, with quantities multiplied through.
 */
export function buildSingleLevelRows(
  nodes: ?Array<Object>,
  parentQtyMultiplier: number = 1,
  level: number = 1,
): Array<FishbowlBomRow> {
  if (!nodes || !nodes.length) {
    return [];
  }

  const rows: Array<FishbowlBomRow> = [];

  nodes.forEach(node => {
    const item = node.tooltip;
    if (!item) {
      return;
    }

    const quantity = (item.usageQty || 0) * parentQtyMultiplier;
    const isPhantomBlowThrough =
      item.isPhantom && node.children && node.children.length > 0;

    if (isPhantomBlowThrough) {
      rows.push(...buildSingleLevelRows(node.children, quantity, level));
    } else {
      rows.push(itemToFishbowlRow(item, quantity, level));
    }
  });

  return rows;
}

/**
 * Indented multi-level BOM — every component at every level.
 */
export function buildIndentedRows(
  nodes: ?Array<Object>,
  level: number = 1,
): Array<FishbowlBomRow> {
  if (!nodes || !nodes.length) {
    return [];
  }

  const rows: Array<FishbowlBomRow> = [];

  nodes.forEach(node => {
    const item = node.tooltip;
    if (!item) {
      return;
    }

    const quantity = item.usageQty || 0;
    rows.push(itemToFishbowlRow(item, quantity, level));

    if (node.children && node.children.length) {
      rows.push(...buildIndentedRows(node.children, level + 1));
    }
  });

  return rows;
}

type FlatEntry = { item: Object, extendedQty: number };

function flattenWithExtendedQty(
  nodes: ?Array<Object>,
  parentQtyMultiplier: number = 1,
): Array<FlatEntry> {
  if (!nodes || !nodes.length) {
    return [];
  }

  const entries: Array<FlatEntry> = [];

  nodes.forEach(node => {
    const item = node.tooltip;
    if (!item) {
      return;
    }

    const extendedQty = (item.usageQty || 0) * parentQtyMultiplier;
    entries.push({ item, extendedQty });

    if (node.children && node.children.length) {
      entries.push(...flattenWithExtendedQty(node.children, extendedQty));
    }
  });

  return entries;
}

/**
 * Summarized BOM — rolls up all levels; same part number is merged with total qty and occur count.
 */
export function buildSummarizedRows(nodes: ?Array<Object>): Array<FishbowlBomRow> {
  const flat = flattenWithExtendedQty(nodes);
  const grouped: {
    [string]: { item: Object, quantity: number, occur: number },
  } = {};

  flat.forEach(({ item, extendedQty }) => {
    const key = item.id || item.itemNumber;
    if (!grouped[key]) {
      grouped[key] = { item, quantity: 0, occur: 0 };
    }
    grouped[key].quantity += extendedQty;
    grouped[key].occur += 1;
  });

  return Object.values(grouped)
    .sort((a, b) =>
      (a.item.itemNumber || '').localeCompare(b.item.itemNumber || ''),
    )
    .map(({ item, quantity, occur }) =>
      itemToFishbowlRow(item, quantity, 0, occur),
    );
}

const SINGLE_LEVEL_COLUMNS: Array<BomPrintColumn> = [
  { key: 'itemNumber', label: 'Component Item Code', align: 'left' },
  { key: 'type', label: 'Type', align: 'left' },
  { key: 'quantity', label: 'Quantity Per Bill', align: 'right' },
  { key: 'uom', label: 'Unit of Measure', align: 'left' },
  { key: 'scrapPercent', label: 'Scrap Percent', align: 'right' },
  { key: 'description', label: 'Description', align: 'left' },
  { key: 'findNumber', label: 'Find Number', align: 'left' },
];

const INDENTED_COLUMNS: Array<BomPrintColumn> = [
  { key: 'levelLabel', label: 'Level', align: 'left' },
  { key: 'itemNumber', label: 'Component Item Code', align: 'left' },
  { key: 'type', label: 'Type', align: 'left' },
  { key: 'quantity', label: 'Quantity Per Parent', align: 'right' },
  { key: 'uom', label: 'Unit of Measure', align: 'left' },
  { key: 'description', label: 'Description', align: 'left' },
  { key: 'findNumber', label: 'Find Number', align: 'left' },
];

const SUMMARIZED_COLUMNS: Array<BomPrintColumn> = [
  { key: 'occur', label: 'Occur', align: 'right' },
  { key: 'itemNumber', label: 'Component Item Code', align: 'left' },
  { key: 'quantity', label: 'Quantity Per Bill', align: 'right' },
  { key: 'uom', label: 'Unit of Measure', align: 'left' },
  { key: 'description', label: 'Description', align: 'left' },
];

const COSTED_COLUMNS: Array<BomPrintColumn> = [
  { key: 'itemNumber', label: 'Component Item Code', align: 'left' },
  { key: 'type', label: 'Type', align: 'left' },
  { key: 'description', label: 'Description', align: 'left' },
  { key: 'uom', label: 'Unit of Measure', align: 'left' },
  { key: 'scrapPercent', label: 'Scrap Percent', align: 'right' },
  { key: 'quantity', label: 'Quantity Per Bill', align: 'right' },
  { key: 'averageCost', label: 'Average Cost', align: 'right' },
  { key: 'costOfBill', label: 'Cost of Bill', align: 'right' },
];

export function buildBomPrintReport(
  reportType: BomPrintReportType,
  treeData: ?Array<Object>,
): BomPrintReport {
  const maxLevel = getMaxTreeLevel(treeData);
  let rows: Array<FishbowlBomRow> = [];
  let columns: Array<BomPrintColumn> = [];
  let totalCost: ?number = null;

  switch (reportType) {
    case 'single-level':
      rows = buildSingleLevelRows(treeData);
      columns = SINGLE_LEVEL_COLUMNS;
      break;
    case 'indented':
      rows = buildIndentedRows(treeData);
      columns = INDENTED_COLUMNS;
      break;
    case 'summarized':
      rows = buildSummarizedRows(treeData);
      columns = SUMMARIZED_COLUMNS;
      break;
    case 'costed':
      rows = buildSingleLevelRows(treeData);
      columns = COSTED_COLUMNS;
      totalCost = rows.reduce((sum, row) => sum + row.costOfBill, 0);
      break;
    default:
      rows = buildSingleLevelRows(treeData);
      columns = SINGLE_LEVEL_COLUMNS;
  }

  let reportSubtitle = REPORT_SUBTITLES[reportType];
  if (reportType === 'indented' || reportType === 'summarized') {
    reportSubtitle =
      maxLevel > 0
        ? i18nLevelSubtitle(maxLevel)
        : '';
  }

  return {
    reportTitle: REPORT_TITLES[reportType],
    reportSubtitle,
    columns,
    rows,
    footerNotes: collectFooterNotes(rows),
    totalCost,
    maxLevel,
  };
}

function i18nLevelSubtitle(maxLevel: number): string {
  return `${maxLevel} Level${maxLevel !== 1 ? 's' : ''}`;
}

export function formatRowCell(
  row: FishbowlBomRow,
  columnKey: string,
): string {
  switch (columnKey) {
    case 'quantity':
      return formatQty(row.quantity);
    case 'scrapPercent':
      return formatScrap(row.scrapPercent);
    case 'averageCost':
      return row.averageCost
        ? `${getCurrencySymbol()}${formatCost(row.averageCost)}`
        : formatCost(0);
    case 'costOfBill':
      return row.costOfBill
        ? `${getCurrencySymbol()}${formatCost(row.costOfBill)}`
        : formatCost(0);
    case 'occur':
      return String(row.occur);
    default:
      return row[columnKey] != null ? String(row[columnKey]) : '';
  }
}

export const BOM_PRINT_REPORT_OPTIONS: Array<{
  value: BomPrintReportType,
  label: string,
}> = [
  { value: 'single-level', label: 'Single-Level BOM' },
  { value: 'indented', label: 'Indented BOM' },
  { value: 'summarized', label: 'Summarized BOM' },
  { value: 'costed', label: 'Costed BOM' },
];
